import type { MouseEvent, ReactNode } from "react";
import Link from "next/link";

import { useAnalytics } from "@/hooks/useCookieUtils";
import { POSTHOG_EVENTS } from "@src/services/posthog/events";
import { captureSeoAttributionContext } from "@src/services/posthog/attribution";
import type { AcquisitionPageType } from "@src/config/productFacts";

type SeoCtaLinkProps = {
  href: string;
  pageType: AcquisitionPageType;
  placement: string;
  competitorSlug?: string;
  className?: string;
  children: ReactNode;
  ariaLabel?: string;
  action?: "signup" | "open_plugin";
};

export default function SeoCtaLink({
  href,
  pageType,
  placement,
  competitorSlug,
  className,
  children,
  ariaLabel,
  action = "signup",
}: SeoCtaLinkProps) {
  const { trackEvent } = useAnalytics();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const landingPage =
      typeof window !== "undefined" ? window.location.pathname : undefined;

    if (landingPage) {
      captureSeoAttributionContext({
        seoPageType: pageType,
        competitorSlug,
        landingPage,
      });
    }

    trackEvent(POSTHOG_EVENTS.CTA_CLICKED, {
      action,
      destination: href,
      seoPageType: pageType,
      competitorSlug,
      ctaPlacement: placement,
      landingPage,
    });

    if (event.defaultPrevented) return;
  };

  return (
    <Link href={href}>
      <a className={className} onClick={handleClick} aria-label={ariaLabel}>
        {children}
      </a>
    </Link>
  );
}
